import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { TokenService } from './token.service';
import { NotificationService } from './notification.service';
import {
  ActualProductiveTimeResponse,
  ProductionOrderTimePayload,
  QuantityProgressResponse,
  RhythmAnalysisResponse,
  TheoreticalTimeResponse,
} from '../../models/production-order.model';
import {
  FinalizationOrderPayload,
  FinalizationOrderResponse,
} from '../../models/finalization-order.model';
import { ToasterService } from './toaster.service';

@Injectable({
  providedIn: 'root',
})
export class ProductionOrderService {
  apiUrl = environment.API_URL + 'internal/';

  constructor(
    private httpClient: HttpClient,
    private tokenService: TokenService,
    private notificationService: NotificationService,
    private toasterService: ToasterService
  ) { }

  getCustomerHeader() {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + this.tokenService.getComponentCodeToken(),
      'Accept-Language': 'pt-BR',
    });
  }

  public validateBarCode(
    branchCode: number,
    productBarCode: string,
    placeOrigin: number | null,
    placeDestination: number | null,
    countCode: number | null,
    productionOrderBranchCode?: number,
    productionOrderCycleCode?: number,
    productionOrderCode?: number,
  ): Observable<any> {
    const url = this.apiUrl + 'production-order/v2/barcode/validate';

    const body = {
      branchCode,
      productBarCode,
      placeOrigin,
      placeDestination,
      countCode,
      productionOrderBranchCode,
      productionOrderCycleCode,
      productionOrderCode,
    };

    const headers = this.getCustomerHeader();

    return this.httpClient.post<any>(url, body, { headers });
  }

  public finalizeOrder(payload: FinalizationOrderPayload): Observable<FinalizationOrderResponse> {
    const url = this.apiUrl + 'production-order/v2/orders/finalization';
    payload.componentId = this.tokenService.getComponentCode('production-order-finalization')!;

    const httpHeaders = this.getCustomerHeader();

    const fetchHeaders: Record<string, string> = {
      'Accept': 'text/event-stream',
      'Content-Type': 'application/json'
    };

    if (httpHeaders && typeof httpHeaders.keys === 'function') {
      httpHeaders.keys().forEach((key: string) => {
        fetchHeaders[key] = httpHeaders.get(key) || '';
      });
    }

    return new Observable<FinalizationOrderResponse>(observer => {

      fetch(url, {
        method: 'POST',
        headers: fetchHeaders,
        body: JSON.stringify(payload)
      }).then(async response => {

        if (!response.body) {
          observer.error(new Error('Corpo da resposta vazio.'));
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const dataStr = line.substring(6).trim();
                if (!dataStr) continue;

                const parsedData = JSON.parse(dataStr);

                if (parsedData.status === 'completed') {

                  if (parsedData.result && parsedData.result.success === false) {
                    observer.error({ error: parsedData.result.errors || [{ message: 'Erro ao processar OP.' }] });
                  } else {
                    observer.next(parsedData.result as FinalizationOrderResponse);
                  }

                  observer.complete();
                  return;
                }
              }
            }
          }
        } catch (err) {
          observer.error(err);
        }
      }).catch(err => observer.error(err));
    });
  }

  public postTimeTrack(payload: ProductionOrderTimePayload): Observable<any> {
    const headers = this.getCustomerHeader();

    return this.httpClient.post<any>(`${this.apiUrl}production-order/v2/time/track`, payload, {
      headers,
    });
  }

  public postTimeHeartbeat(payload: ProductionOrderTimePayload): Observable<any> {
    const headers = this.getCustomerHeader();

    return this.httpClient.post<any>(`${this.apiUrl}production-order/v2/time/heartbeat`, payload, {
      headers,
    });
  }

  public getTheoreticalTime(
    branchCode: number,
    cycleCode: number,
    orderCode: number,
  ): Observable<TheoreticalTimeResponse> {
    const url = `${this.apiUrl}production-order/v2/orders/${branchCode}/${cycleCode}/${orderCode}/theoretical-time`;
    const headers = this.getCustomerHeader();

    return this.httpClient.get<TheoreticalTimeResponse>(url, { headers });
  }

  public getActualProductiveTime(
    branchCode: number,
    cycleCode: number,
    orderCode: number,
  ): Observable<ActualProductiveTimeResponse> {
    const url = `${this.apiUrl}production-order/v2/orders/${branchCode}/${cycleCode}/${orderCode}/actual-time`;
    const headers = this.getCustomerHeader();

    return this.httpClient.get<ActualProductiveTimeResponse>(url, { headers });
  }

  public getQuantityProgress(
    branchCode: number,
    cycleCode: number,
    orderCode: number,
  ): Observable<QuantityProgressResponse> {
    const url = `${this.apiUrl}production-order/v2/orders/${branchCode}/${cycleCode}/${orderCode}/quantity-progress`;
    const headers = this.getCustomerHeader();

    return this.httpClient.get<QuantityProgressResponse>(url, { headers });
  }

  public getRhythmAnalysis(
    branchCode: number,
    cycleCode: number,
    orderCode: number,
  ): Observable<RhythmAnalysisResponse> {
    const url = `${this.apiUrl}production-order/v2/orders/${branchCode}/${cycleCode}/${orderCode}/rhythm-analysis`;
    const headers = this.getCustomerHeader();

    return this.httpClient.get<RhythmAnalysisResponse>(url, { headers });
  }

}
